/** Widget が fullsize に降格する境界の幅。これ未満にしない */
export const MIN_WINDOW_WIDTH = 360
export const DEFAULT_WINDOW_WIDTH = 480
/** 窓の右端からはみ出す幅変更つまみの幅（画面内に残すために空ける） */
export const RESIZE_HANDLE_WIDTH = 20

/** 動画部分の縦横比 */
export const ASPECT_RATIO_VIDEO = 16 / 9
/** Widget の枠の左右の合計（3px x 2）。動画部分の幅は、窓の幅からこれを引いた値 */
export const WIDGET_CHROME_WIDTH = 6
// 初期位置は「接続のやり方」タブ（右端固定・幅 140）の真横に置く
const SETUP_TAB_WIDTH = 140
const INITIAL_BOTTOM_MARGIN = 10
// Widget の window:resized が届く前の見積もり（ヘッダー 41 + フッター閉 38 + 枠 3 x 2）
const DEFAULT_EXTRA_HEIGHT = 41 + 38 + 6

export interface ReportedSize {
  readonly width: number
  readonly height: number
}

interface Size {
  readonly width: number
  readonly height: number
}

interface Position {
  readonly x: number
  readonly y: number
}

/**
 * iframe ホストの高さ。Widget が最後に通知した高さから「動画以外の高さ」
 * （ヘッダー・フッター・枠）を求め、現在の幅に 16:9 の動画高さを足す。
 * 通知が無ければ既定値で見積もる
 */
export const estimateFrameHeight = (
  width: number,
  lastReport: ReportedSize | null,
): number => {
  const extraHeight =
    lastReport === null ? DEFAULT_EXTRA_HEIGHT : (
      lastReport.height - lastReport.width / ASPECT_RATIO_VIDEO
    )
  return width / ASPECT_RATIO_VIDEO + extraHeight
}

/** 動画部分の高さ。窓の幅から枠（左右）を引いた幅の 16:9 */
export const getVideoAreaHeight = (width: number): number =>
  (width - WIDGET_CHROME_WIDTH) / ASPECT_RATIO_VIDEO

/**
 * 辺・角のリサイズで 16:9 を保つときの、窓の高さのうち動画部分以外
 * （ヘッダー・チャプター一覧・枠）の高さ。直近の窓の大きさ（幅と、その幅での
 * 窓の高さ）から逆算する。一覧の開閉で窓の高さが変わると、この値も変わる
 */
export const getAspectLockExtraHeight = (
  width: number,
  windowHeight: number,
): number => windowHeight - getVideoAreaHeight(width)

/** リサイズで下回らない窓の高さ。最小の幅での動画部分の高さに、動画以外の高さを足す */
export const getMinWindowHeight = (extraHeight: number): number =>
  getVideoAreaHeight(MIN_WINDOW_WIDTH) + extraHeight

/**
 * 窓が画面内に収まる位置に丸める（右側のつまみ分を空ける）。
 * 窓が画面より大きいときは、左上（下限 0）を優先する
 */
export const clampPosition = (
  position: Position,
  size: Size,
  viewport: Size,
): Position => {
  const maxX = viewport.width - size.width - RESIZE_HANDLE_WIDTH
  const maxY = viewport.height - size.height
  return {
    x: Math.max(0, Math.min(position.x, maxX)),
    y: Math.max(0, Math.min(position.y, maxY)),
  }
}

/** 初期位置（「接続のやり方」タブの真横。画面の下端から少し上） */
export const getInitialPosition = (size: Size, viewport: Size): Position =>
  clampPosition(
    {
      x: viewport.width - size.width - RESIZE_HANDLE_WIDTH - SETUP_TAB_WIDTH,
      y: viewport.height - size.height - INITIAL_BOTTOM_MARGIN,
    },
    size,
    viewport,
  )
